import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../../layout/AdminLayout';
import { useAuthContext } from '../../../../context/AuthContext';
import { HTTPResponse } from '@/class/HTTPResponse';
import { Option } from '@/class/Option';
import { fetchData, putData } from '../../../../lib/api';
import {
  adminCard,
  adminSectionTitle,
  adminMutedText,
  adminButtonPrimary,
  adminPageToolbar,
  adminSectionTitleToolbar,
  adminTableWrap,
  adminTable,
  adminTableHead,
  adminTableCell,
  adminInput,
} from '../../../layout/adminStyles';

type PlanItem = {
  id: number;
  name: string;
  furigana: string | null;
  category_id: number | null;
  category_name: string | null;
};

type PlanItemsData = {
  grade: number;
  selectable_levels: number[];
  limit: number;
  selected_item_ids: number[];
  items: PlanItem[];
};

const GRADE_LABELS: Record<number, string> = {
  1: 'すりむにライト',
  2: 'すりむにスタンダード',
  3: 'すりむにプレミアム',
};

const AdminStorePlanItemsPage = () => {
  const { store } = useAuthContext();
  const storeId = store?.id;

  const [data, setData] = useState<PlanItemsData | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [keyword, setKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadPlanItems = useCallback(async () => {
    if (!storeId) {
      setData(null);
      setSelectedIds([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const res: HTTPResponse = await fetchData(`store/${storeId}/plan-items`);
      const payload = res.data as PlanItemsData;
      setData(payload);
      setSelectedIds(Array.isArray(payload.selected_item_ids) ? payload.selected_item_ids : []);
    } catch (err: unknown) {
      console.error(err);
      setData(null);
      setSelectedIds([]);
      setErrorMessage('プラン商品の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadPlanItems();
  }, [loadPlanItems]);

  const categoryOptions: Option[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const map = new Map<string, string>();
    data.items.forEach((item) => {
      if (item.category_id == null || !item.category_name) {
        return;
      }
      map.set(String(item.category_id), item.category_name);
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }, [data]);

  const filteredItems = useMemo(() => {
    if (!data) {
      return [];
    }

    const q = keyword.trim().toLowerCase();

    return data.items.filter((item) => {
      if (selectedCategory && String(item.category_id) !== selectedCategory) {
        return false;
      }

      if (!q) {
        return true;
      }

      const haystack = `${item.name} ${item.furigana ?? ''} ${item.category_name ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [data, keyword, selectedCategory]);

  const canSelect = data != null && (data.selectable_levels?.length ?? 0) > 0 && data.limit > 0;
  const gradeLabel = data ? GRADE_LABELS[data.grade] ?? String(data.grade) : '—';

  const toggleItem = (itemId: number) => {
    if (!canSelect || !data) {
      return;
    }

    setSelectedIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      if (prev.length >= data.limit) {
        alert(`追加商品は${data.limit}個まで選択できます。`);
        return prev;
      }
      return [...prev, itemId];
    });
  };

  const handleSave = async () => {
    if (!storeId || !canSelect) {
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    try {
      const res: HTTPResponse = await putData(`store/${storeId}/plan-items`, {
        item_ids: selectedIds,
      });
      const payload = res.data as PlanItemsData;
      setData(payload);
      setSelectedIds(Array.isArray(payload.selected_item_ids) ? payload.selected_item_ids : []);
      alert('保存しました。');
    } catch (err: unknown) {
      console.error(err);
      const message =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
          ? (err as { message: string }).message
          : '保存に失敗しました。';
      setErrorMessage(message);
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className={adminPageToolbar}>
        <h2 className={adminSectionTitleToolbar}>プラン商品</h2>
        {canSelect && (
          <button
            type="button"
            className={adminButtonPrimary}
            onClick={handleSave}
            disabled={isSaving || isLoading}
          >
            {isSaving ? '保存中...' : '選択を保存'}
          </button>
        )}
      </div>

      <section className={`${adminCard} mb-4`}>
        <h3 className={adminSectionTitle}>登録プランの追加商品</h3>
        <p className={`${adminMutedText} text-sm leading-relaxed`}>
          LIGHT商品はすべてのプランで利用できます。すりむにスタンダードはLIGHT＋その他商品から最大20個、すりむにプレミアムはLIGHT＋その他商品から最大50個を追加選択できます。
        </p>
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-stone-200 bg-stone-50/80 px-4 py-3">
            <dt className="text-xs text-stone-500">現在のプラン</dt>
            <dd className="mt-1 text-base font-medium text-stone-800">{gradeLabel}</dd>
          </div>
          <div className="rounded-xl border border-stone-200 bg-stone-50/80 px-4 py-3">
            <dt className="text-xs text-stone-500">選択上限</dt>
            <dd className="mt-1 text-base font-medium text-stone-800">
              {canSelect ? `${data?.limit ?? 0}個` : '—'}
            </dd>
          </div>
          <div className="rounded-xl border border-stone-200 bg-stone-50/80 px-4 py-3">
            <dt className="text-xs text-stone-500">選択中</dt>
            <dd className="mt-1 text-base font-medium text-stone-800">
              {canSelect ? `${selectedIds.length} / ${data?.limit ?? 0}` : '—'}
            </dd>
          </div>
        </dl>
        {errorMessage && <p className="mt-3 text-sm text-red-600">{errorMessage}</p>}
      </section>

      {!canSelect && !isLoading && (
        <section className={adminCard}>
          <p className={adminMutedText}>
            現在のプラン（{gradeLabel}）では追加商品の選択は不要です。すりむにスタンダードまたはすりむにプレミアムプランでご利用ください。
          </p>
        </section>
      )}

      {canSelect && (
        <section className={adminCard}>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <h3 className={`${adminSectionTitleToolbar} mb-0`}>選択可能な商品</h3>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <div className="w-full sm:w-48">
                <label htmlFor="plan-item-category" className="mb-1 block text-xs font-medium text-stone-600">
                  カテゴリー
                </label>
                <select
                  id="plan-item-category"
                  className={`${adminInput} w-full`}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">すべて</option>
                  {categoryOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-64">
                <label htmlFor="plan-item-keyword" className="mb-1 block text-xs font-medium text-stone-600">
                  キーワード
                </label>
                <input
                  id="plan-item-keyword"
                  type="search"
                  className={`${adminInput} w-full`}
                  placeholder="商品名・フリガナで検索"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <p className={adminMutedText}>読み込み中...</p>
          ) : (
            <div className={adminTableWrap}>
              <table className={adminTable}>
                <thead className={adminTableHead}>
                  <tr>
                    <th className={`${adminTableCell} w-16 text-center`}>選択</th>
                    <th className={adminTableCell}>カテゴリ</th>
                    <th className={adminTableCell}>商品名</th>
                    <th className={adminTableCell}>フリガナ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td className={adminTableCell} colSpan={4}>
                        対象商品がありません。
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const checked = selectedIds.includes(item.id);
                      return (
                        <tr key={item.id} className="hover:bg-stone-50/80">
                          <td className={`${adminTableCell} text-center`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleItem(item.id)}
                              aria-label={`${item.name}を選択`}
                            />
                          </td>
                          <td className={adminTableCell}>{item.category_name ?? '—'}</td>
                          <td className={adminTableCell}>{item.name}</td>
                          <td className={adminTableCell}>{item.furigana ?? '—'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </AdminLayout>
  );
};

export default AdminStorePlanItemsPage;
